import React, { createContext, useContext, ReactNode } from 'react';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  isAuthenticated: boolean;
  hasRole: (roles: string[]) => boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  user: User | null;
  setUser: (user: User | null) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, user, setUser }) => {
  const isAuthenticated = !!user;

  const hasRole = (roles: string[]): boolean => {
    if (!user) return false;
    return user.roles.some(role => roles.includes(role));
  };

  const logout = async (): Promise<void> => {
    try {
      // AuthService für Logout verwenden
      const { AuthService } = await import('../services/authService');
      await AuthService.logout();
      setUser(null);
    } catch (error) {
      // Auch bei Fehler User zurücksetzen
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    setUser,
    isAuthenticated,
    hasRole,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 